import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Leaf } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Shop' },
    { path: '#about', label: 'About' }
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 nav-blur shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-[hsl(var(--charcoal))]">
              Easecarry <span className="text-[hsl(var(--earth-green))] italic">Green</span>
            </span>
          </Link>

          <div className="flex items-center gap-6">
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map(link => (
                link.path.startsWith('#') ? (
                  <a
                    key={link.path}
                    href={link.path}
                    className="font-accent font-semibold text-sm uppercase tracking-wider text-[hsl(var(--stone-gray))] hover:text-[hsl(var(--earth-green))] transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={`font-accent font-semibold text-sm uppercase tracking-wider transition-colors ${
                      location.pathname === link.path
                        ? 'text-[hsl(var(--earth-green))]'
                        : 'text-[hsl(var(--stone-gray))] hover:text-[hsl(var(--earth-green))]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            <Link
              to="/checkout"
              data-testid="cart-button"
              className="relative p-2 rounded-full hover:bg-[hsl(var(--leaf-light))] transition-colors"
            >
              <ShoppingCart className="w-6 h-6 text-[hsl(var(--earth-green))]" />
              {cartCount > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[hsl(var(--sunrise-coral))] text-white text-xs font-bold rounded-full flex items-center justify-center"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[hsl(var(--charcoal))]" />
              ) : (
                <Menu className="w-6 h-6 text-[hsl(var(--charcoal))]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-[hsl(var(--sand))]">
          <div className="container mx-auto px-6 py-4 flex flex-col gap-4">
            {navLinks.map(link => (
              link.path.startsWith('#') ? (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-accent font-semibold text-sm uppercase tracking-wider text-[hsl(var(--stone-gray))]"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-accent font-semibold text-sm uppercase tracking-wider ${
                    location.pathname === link.path
                      ? 'text-[hsl(var(--earth-green))]'
                      : 'text-[hsl(var(--stone-gray))]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;